import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Select, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type {
  ApplicationRow, ApplicationStatus, CreateApplicationRequest,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Applications.
 * Route: /admin/admissions/applications          → list + new-application modal
 *        /admin/admissions/applications/:code    → detail + status decisions (verify → interview → offered → accepted/rejected)
 * Query params on the list route pre-fill the new-application modal so
 * "convert inquiry" from the Inquiries page seeds the form.
 */
const STATUS_TONE: Record<ApplicationStatus, string> = {
  submitted: 'processing',
  verified: 'processing',
  interview: 'warning',
  offered: 'purple',
  accepted: 'success',
  rejected: 'default',
  withdrawn: 'default',
};

export function ApplicationVerificationPage() {
  const { institution = '', code } = useParams<{ institution: string; code?: string }>();
  if (code) return <ApplicationDetail institution={institution} code={code} />;
  return <ApplicationsList institution={institution} />;
}

function ApplicationsList({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);

  // If we arrived with `?inquiry=` params from Inquiries page, auto-open the modal.
  useEffect(() => {
    if (sp.get('inquiry') || sp.get('name')) setAddOpen(true);
  }, [sp]);

  const list = useQuery({
    queryKey: ['applications', institution],
    queryFn: () => productApi.listApplications(institution),
    enabled: !!institution,
  });

  const rows = list.data?.rows ?? [];
  const by = list.data?.by_status ?? { submitted: 0, verified: 0, interview: 0, offered: 0, accepted: 0, rejected: 0, withdrawn: 0 };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Applications</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Submitted → verified → interview → offered → accepted (admits a real Student).
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New application</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8 }}>
        {(['submitted', 'verified', 'interview', 'offered', 'accepted', 'rejected', 'withdrawn'] as ApplicationStatus[]).map((s) => (
          <Card key={s} size="small"><Statistic title={s.charAt(0).toUpperCase() + s.slice(1)} value={by[s]} /></Card>
        ))}
      </div>

      <Card size="small" title={`${rows.length} applications`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No applications. <a onClick={() => setAddOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<ApplicationRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            onRow={(r) => ({ onClick: () => navigate(`/i/${institution}/admin/admissions/applications/${r.code}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Code', dataIndex: 'code', key: 'c', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Child', dataIndex: 'child_name', key: 'n', render: (v: string) => <b>{v}</b> },
              { title: 'Grade', dataIndex: 'grade_applied', key: 'g', width: 80 },
              { title: 'Guardian', dataIndex: 'guardian_name', key: 'gn' },
              { title: 'Phone', dataIndex: 'guardian_phone', key: 'p', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Status', dataIndex: 'status', key: 's', width: 110, render: (v: ApplicationStatus) => <Tag color={STATUS_TONE[v]}>{v}</Tag> },
            ]}
          />
        )}
      </Card>

      <NewApplicationModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setSp({});
        }}
        institution={institution}
        seed={{
          inquiry_id: sp.get('inquiry') ?? undefined,
          child_name: sp.get('name') ?? undefined,
          guardian_name: sp.get('parent') ?? undefined,
          guardian_phone: sp.get('phone') ?? undefined,
          grade_applied: sp.get('grade') ?? undefined,
        }}
        onCreated={(code) => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['applications', institution] });
          void message.success('Application created');
          navigate(`/i/${institution}/admin/admissions/applications/${code}`);
        }}
      />
    </Space>
  );
}

function NewApplicationModal({
  open, institution, seed, onClose, onCreated,
}: {
  open: boolean; institution: string;
  seed: { inquiry_id?: string; child_name?: string; guardian_name?: string; guardian_phone?: string; grade_applied?: string };
  onClose: () => void; onCreated: (code: string) => void;
}) {
  const [form] = Form.useForm<{
    child_name: string; gender: 'male' | 'female' | 'other'; dob: string;
    grade_applied: string; guardian_name: string; guardian_phone: string; guardian_email?: string;
  }>();
  const m = useMutation({
    mutationFn: (v: CreateApplicationRequest) => productApi.createApplication(institution, v),
    onSuccess: (r) => { form.resetFields(); onCreated(r.code); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="New application" onCancel={() => { m.reset(); onClose(); }} okText="Create" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{
          child_name: seed.child_name ?? '',
          guardian_name: seed.guardian_name ?? '',
          guardian_phone: seed.guardian_phone ?? '',
          grade_applied: seed.grade_applied ?? '',
          gender: 'male',
        }}
        onFinish={(v) => m.mutate({
          inquiry_id: seed.inquiry_id,
          child_name: v.child_name.trim(),
          gender: v.gender,
          dob: v.dob,
          grade_applied: v.grade_applied.trim(),
          guardian_name: v.guardian_name.trim(),
          guardian_phone: v.guardian_phone.trim(),
          guardian_email: v.guardian_email?.trim() || undefined,
        })}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="child_name" label="Child name" rules={[{ required: true }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item name="grade_applied" label="Grade applied for" rules={[{ required: true }]}>
            <Input placeholder="9" />
          </Form.Item>
          <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
            <Select options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]} />
          </Form.Item>
          <Form.Item name="dob" label="Date of birth" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}>
            <Input placeholder="yyyy-mm-dd" />
          </Form.Item>
          <Form.Item name="guardian_name" label="Guardian name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="guardian_phone" label="Guardian phone" rules={[{ required: true }, { pattern: /^[+0-9 -]{6,20}$/, message: 'digits, spaces, plus or dash' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="guardian_email" label="Guardian email" rules={[{ type: 'email', message: 'invalid email' }]}>
            <Input />
          </Form.Item>
        </div>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function ApplicationDetail({ institution, code }: { institution: string; code: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [decideStatus, setDecideStatus] = useState<ApplicationStatus | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');

  const q = useQuery({
    queryKey: ['application', institution, code],
    queryFn: () => productApi.getApplication(institution, code),
    enabled: !!institution && !!code,
    retry: false,
  });
  const update = useMutation({
    mutationFn: () => productApi.updateApplicationStatus(institution, code, {
      status: decideStatus!,
      decision_note: decisionNote.trim() || undefined,
      admission_number: decideStatus === 'accepted' ? admissionNumber.trim() : undefined,
    }),
    onSuccess: (r) => {
      setDecideStatus(null);
      setDecisionNote('');
      setAdmissionNumber('');
      void qc.invalidateQueries({ queryKey: ['application', institution, code] });
      void qc.invalidateQueries({ queryKey: ['applications', institution] });
      void qc.invalidateQueries({ queryKey: ['students', institution] });
      void qc.invalidateQueries({ queryKey: ['briefing', institution] });
      if (r.admitted_student_id) void message.success('Admitted!');
      else void message.success('Status updated');
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Update failed'),
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Application not found" />;
  const app = q.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/admissions/applications`)}>← Applications</Button>
        <Typography.Text type="secondary">/</Typography.Text>
        <Typography.Text style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{app.code}</Typography.Text>
      </Space>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>{app.child_name}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Grade {app.grade_applied} · {app.gender} · DOB {app.dob}
          </Typography.Text>
        </div>
        <Space>
          <Tag color={STATUS_TONE[app.status]}>{app.status}</Tag>
        </Space>
      </div>

      <Card size="small" title="Guardian">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div><b>Name</b><div>{app.guardian_name}</div></div>
          <div><b>Phone</b><div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{app.guardian_phone}</div></div>
          <div><b>Email</b><div>{app.guardian_email ?? '—'}</div></div>
        </div>
      </Card>

      {app.decision_note && (
        <Alert type="info" showIcon message="Last decision note" description={app.decision_note} />
      )}

      <Card size="small" title="Actions">
        <Space wrap>
          {(['verified', 'interview', 'offered', 'accepted', 'rejected', 'withdrawn'] as ApplicationStatus[])
            .filter((s) => s !== app.status && !(app.status === 'accepted' && s !== 'withdrawn'))
            .map((s) => (
              <Button key={s} onClick={() => { setDecideStatus(s); setDecisionNote(''); setAdmissionNumber(''); }}>
                → {s}
              </Button>
            ))}
        </Space>
        {app.admitted_student_id && (
          <div style={{ marginTop: 12 }}>
            <Tag color="success">Admitted — student record created</Tag>
          </div>
        )}
      </Card>

      <Modal
        open={!!decideStatus}
        title={`Set status → ${decideStatus}`}
        onCancel={() => setDecideStatus(null)}
        okText={decideStatus === 'accepted' ? 'Accept and admit' : 'Update'}
        confirmLoading={update.isPending}
        onOk={() => update.mutate()}
        destroyOnClose
      >
        {decideStatus === 'accepted' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Accepting creates a real Student record"
            description="A guardian record is created too, marked primary. The application locks after this."
          />
        )}
        {decideStatus === 'accepted' && (
          <Form layout="vertical">
            <Form.Item label="Admission number" required>
              <Input value={admissionNumber} onChange={(e) => setAdmissionNumber(e.target.value)} placeholder="2026-0001" autoFocus />
            </Form.Item>
          </Form>
        )}
        <Form layout="vertical">
          <Form.Item label="Decision note (optional)">
            <Input.TextArea rows={3} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder="Reason, follow-ups…" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
